import { useMemo } from 'react';
import { useMyConversations } from '@/hooks/useMyConversations';
import { useInquiries } from '@/hooks/useInquiries';
import { useNotifications } from '@/hooks/useNotifications';
import { useTeamConversation } from '@/hooks/useTeamConversation';
import { isShadowedByConversation } from '@/lib/notificationShadowing';

/**
 * Single source of truth for inbox unread counters. Consumed by the chat
 * panel minimized pill, ChatBubble, and BottomNav so all three surfaces
 * stay in lockstep instead of each re-deriving from raw queries.
 *
 * Suppresses notification types that are already represented by a DM /
 * inquiry row in the inbox via `isShadowedByConversation` so the badge
 * doesn't double-count.
 */

export function useInboxUnreadCounters() {
	const { unreadCount: dmUnread } = useMyConversations();
	const { data: inquiries = [] } = useInquiries();
	const { data: notifications = [] } = useNotifications();
	const { unread: teamUnread } = useTeamConversation();

	return useMemo(() => {
		const inquiryUnread = inquiries.filter((i: any) => !i.read_at).length;
		const notifUnread = notifications.filter(
			(n: any) => !n.read && !isShadowedByConversation(n.type),
		).length;
		const total = dmUnread + inquiryUnread + notifUnread + (teamUnread ? 1 : 0);
		return {
			dmUnread,
			inquiryUnread,
			notifUnread,
			teamUnread: !!teamUnread,
			total,
		};
	}, [dmUnread, inquiries, notifications, teamUnread]);
}
